import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDownIcon } from './Icons';

export interface SelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

interface TeslaSelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  showRequired?: boolean;
  error?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
  searchable?: boolean; // Enable search/filter functionality
  searchPlaceholder?: string; // Placeholder for search input
}

/**
 * Tesla-inspired custom select component
 */
const TeslaSelect: React.FC<TeslaSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  label,
  showRequired,
  error,
  fullWidth = false,
  disabled = false,
  className = '',
  id,
  searchable = false,
  searchPlaceholder = 'Type to search...',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;
  const labelId = `${selectId}-label`;
  const triggerId = `${selectId}-trigger`;
  const listboxId = `${selectId}-listbox`;
  const errorId = error ? `${selectId}-error` : undefined;
  
  // Validate options array
  if (!Array.isArray(options) || options.length === 0) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('TeslaSelect: options array is empty or invalid');
    }
  }
  
  // Find the currently selected option
  const selectedOption = options.find(option => option?.value === value);
  
  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    try {
      if (!Array.isArray(options) || options.length === 0) {
        return [];
      }

      if (searchable && searchQuery.trim() !== '') {
        const query = searchQuery.toLowerCase().trim();
        return options
          .filter(option => 
            option?.label?.toLowerCase().startsWith(query) ||
            option?.value?.toLowerCase().startsWith(query)
          )
          .filter(Boolean) // Remove any null/undefined entries
          .sort((a, b) => a.label.localeCompare(b.label)); // Sort alphabetically
      }
      
      return options;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('TeslaSelect: Error filtering options', error);
      }
      return options; // Fallback to all options
    }
  }, [options, searchable, searchQuery]);
  
  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery(''); // Clear search when closing
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Focus search input when dropdown opens (if searchable)
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      // Small delay to ensure the input is rendered
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, searchable]);
  
  // Handle option selection
  const handleOptionClick = (option: SelectOption) => {
    try {
      if (!option || !option.value) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('TeslaSelect: Invalid option selected', option);
        }
        return;
      }

      onChange(option.value);
      setIsOpen(false);
      setSearchQuery(''); // Clear search after selection
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('TeslaSelect: Error selecting option', error);
      }
    }
  };
  
  // Toggle dropdown open/close
  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchQuery(''); // Clear search when opening
      }
    }
  };
  
  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };
  
  // Clear search
  const handleClearSearch = () => {
    setSearchQuery('');
    searchInputRef.current?.focus();
  };
  
  // Determine classes for the select container
  const containerClasses = [
    'select-container',
    isOpen ? 'select-container--open' : '',
    error ? 'select-container--error' : '',
    fullWidth ? 'select-container--full' : '',
    disabled ? 'select-container--disabled' : '',
    className
  ].filter(Boolean).join(' ');
  
  return (
    <div className={containerClasses} ref={containerRef}>
      {label && (
        <label id={labelId} htmlFor={triggerId} className="select-label">
          {label}
          {showRequired ? <span className="input-label-required" aria-hidden> *</span> : null}
        </label>
      )}
      
      <div 
        className="select-control"
        onClick={toggleDropdown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-labelledby={label ? labelId : undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={errorId}
        aria-required={showRequired ? true : undefined}
        id={triggerId}
      >
        <div className="select-value">
          {selectedOption ? (
            <div className="select-option-content">
              {selectedOption.icon && (
                <span className="select-option-icon">{selectedOption.icon}</span>
              )}
              <span className="select-option-label">{selectedOption.label}</span>
            </div>
          ) : (
            <span className="select-placeholder">{placeholder}</span>
          )}
        </div>
        
        <div className="select-indicator">
          <ChevronDownIcon />
        </div>
      </div>
      
      {isOpen && (
        <div className="select-menu" role="listbox" id={listboxId}>
          {searchable && (
            <div className="select-search">
              <input
                ref={searchInputRef}
                type="text"
                className="select-search-input"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={handleSearchChange}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  // Prevent closing dropdown when typing
                  if (e.key === 'Escape') {
                    setIsOpen(false);
                    setSearchQuery('');
                  }
                }}
              />
              {searchQuery && (
                <button
                  type="button"
                  className="select-search-clear"
                  onClick={handleClearSearch}
                  aria-label="Clear search">
                  ×
                </button>
              )}
            </div>
          )}
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <div
                key={option.value}
                className={`select-option ${option.value === value ? 'select-option--selected' : ''}`}
                onClick={() => handleOptionClick(option)}
                role="option"
                aria-selected={option.value === value}
              >
                <div className="select-option-content">
                  {option.icon && (
                    <span className="select-option-icon">{option.icon}</span>
                  )}
                  <span className="select-option-label">{option.label}</span>
                </div>
              </div>
            ))
          ) : (
            <div className="select-option select-option--empty">
              <span className="select-option-label">No options found</span>
            </div>
          )}
        </div>
      )}
      
      {error && (
        <div className="select-error" id={errorId} role="alert">
          {error}
        </div>
      )}
    </div>
  );
};

export default TeslaSelect;